// src/components/NavBar.js
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, Menu, X, MessageCircle, Users, Lightbulb } from "lucide-react";
import { BondLockup } from "./BondMark";

const BondCtaMark = () => (
  <span className="relative inline-flex h-5 w-8 items-center justify-center" aria-hidden>
    <span className="absolute left-1 h-2 w-2 rounded-full bg-white" />
    <span className="absolute right-1 h-2 w-2 rounded-full bg-white" />
    <span className="absolute left-[9px] right-[9px] h-px bg-white/75" />
    <span className="absolute left-[8px] right-[8px] top-1.5 h-2 rounded-full border-t border-white/60" />
  </span>
);

const NavBar = ({ onContactUsClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollState, setScrollState] = useState({
    isScrolled: false,
    isVisible: true,
    lastScrollY: 0,
  });

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const isScrolled = currentScrollY > 20;

    setScrollState((prev) => ({
      isScrolled,
      isVisible: currentScrollY < prev.lastScrollY || currentScrollY < 100,
      lastScrollY: currentScrollY,
    }));
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleMobileContactClick = (e) => {
    e.preventDefault();
    setIsOpen(false);
    setTimeout(() => onContactUsClick(), 300);
  };

  return (
    <nav
      className={`
        fixed w-full z-50 transition-all duration-500 ease-in-out
        ${scrollState.isVisible ? "translate-y-0" : "-translate-y-full"}
        ${
          scrollState.isScrolled
            ? "bg-black/70 backdrop-blur-md border-b border-white/[0.06]"
            : "bg-transparent"
        }
      `}
    >
      <div className="container mx-auto px-4">
        <div className="relative flex justify-between items-center h-16 md:h-20">
          <Link
            to="/"
            className="group relative inline-flex items-center transition-opacity duration-300 hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E88E5] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
            aria-label="BetBond home"
          >
            <BondLockup
              size={24}
              textClassName="text-2xl sm:text-3xl"
              animated
            />
          </Link>

          <div className="hidden md:flex items-center gap-2 ml-auto">
            <Link
              to="/app/p2p"
              className="inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium text-white/75 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              <Users size={15} />
              <span>P2P</span>
            </Link>
            <Link
              to="/app/predictions"
              className="inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium text-white/75 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              <Lightbulb size={15} />
              <span>Tips</span>
            </Link>
            <button
              onClick={onContactUsClick}
              type="button"
              className="inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium text-white/75 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              <MessageCircle size={15} />
              <span>Contact</span>
            </button>

            <Link
              to="/download"
              className="inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium text-white/75 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              <Download size={15} />
              <span>Download app</span>
            </Link>

            <Link
              to="/app/welcome"
              className="inline-flex items-center gap-2.5 h-10 rounded-full border border-[#64B5F6]/50 bg-gradient-to-b from-[#1E88E5] to-[#1565C0] pl-3 pr-4 text-sm font-extrabold tracking-tight text-white shadow-[0_6px_18px_-8px_rgba(30,136,229,0.75)] hover:brightness-110 transition-all"
              aria-label="Go to BetBond web app"
            >
              <BondCtaMark />
              <span>Go Bond</span>
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors duration-300 ease-out"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={20} className="text-white" /> : <Menu size={20} className="text-white" />}
          </button>

          {isOpen && (
            <div className="absolute top-16 left-0 w-full bg-black/95 backdrop-blur-md border-t border-white/[0.06] shadow-xl p-4 space-y-2 md:hidden">
              <Link
                to="/app/welcome"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-full border border-[#64B5F6]/50 bg-gradient-to-b from-[#1E88E5] to-[#1565C0] text-white text-sm font-extrabold shadow-[0_6px_18px_-8px_rgba(30,136,229,0.75)] hover:brightness-110 transition-all"
                aria-label="Go to BetBond web app"
              >
                <BondCtaMark />
                <span>Go Bond</span>
              </Link>

              <Link
                to="/app/p2p"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white/80 hover:text-white hover:bg-white/[0.04] transition-colors text-sm"
              >
                <Users size={16} />
                <span>P2P matching</span>
              </Link>
              <Link
                to="/app/predictions"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white/80 hover:text-white hover:bg-white/[0.04] transition-colors text-sm"
              >
                <Lightbulb size={16} />
                <span>Tips</span>
              </Link>
              <Link
                to="/download"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white/80 hover:text-white hover:bg-white/[0.04] transition-colors text-sm"
              >
                <Download size={16} />
                <span>Download Android app</span>
              </Link>

              <button
                type="button"
                onClick={handleMobileContactClick}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white/80 hover:text-white hover:bg-white/[0.04] transition-colors text-sm"
              >
                <MessageCircle size={16} />
                <span>Contact</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
